import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NotificationService } from '../../notification/notification.service';

/** A modálnak átadott termék: a fizikai készletet a stock+reserved adja. */
export interface StockReallocProduct {
  id: number;
  sku: string;
  name: string;
  stock?: number;
  reserved?: number;
}

interface ReallocOrderRow {
  orderId: number;
  displayId: string;
  customer: string;
  country: string;
  placedLabel: string;
  days: number | null;
  need: number;
  alloc: number;
  auto: boolean;
}

/**
 * Készlet-átruházás modál — közös komponens (Készlet oldal és Rendelés
 * részletek). A rendszer időrendben (FIFO) foglalja le a készletet a
 * rendelések közt; itt kézzel át lehet rendezni, ki melyik darabot kapja.
 */
@Component({
  selector: 'app-stock-realloc',
  templateUrl: './stock-realloc.component.html',
  styleUrl: './stock-realloc.component.scss',
})
export class StockReallocComponent implements OnChanges {
  /** Ha nem null, a modál nyitva van és betölti az adott termékhez a rendeléseket. */
  @Input() product: StockReallocProduct | null = null;
  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  stock = 0;
  orders: ReallocOrderRow[] = [];
  draft: ReallocOrderRow[] = [];
  loading = false;

  constructor(
    private http: HttpClient,
    private notificationService: NotificationService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['product']) {
      if (this.product) {
        this.open();
      } else {
        this.orders = [];
        this.draft = [];
      }
    }
  }

  private open() {
    const product = this.product!;
    this.stock = Math.max(0, (product.stock || 0) + (product.reserved || 0));
    this.orders = [];
    this.draft = [];
    this.loading = true;

    const params = new URLSearchParams();
    params.set('search', product.sku || product.name || '');
    params.set('only_not_packaged', '1');
    params.set('archived', '0');
    params.set('sort_by', 'created_at');
    params.set('page', '0');
    params.set('page_size', '100');

    this.http.get<{ items: any[] }>(`orders?${params.toString()}`).subscribe({
      next: (response) => {
        const orders = (response?.items || [])
          .slice()
          .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

        const rows: ReallocOrderRow[] = [];
        let remaining = this.stock;
        for (const order of orders) {
          const need = (order.orderProducts || [])
            .filter((op: any) => op.component_id === product.id || op.sku === product.sku)
            .reduce((sum: number, op: any) => sum + (op.quantity || 0), 0);
          if (need <= 0) {
            continue;
          }
          const alloc = Math.max(0, Math.min(need, remaining));
          remaining -= alloc;
          rows.push({
            orderId: order.id,
            displayId: '#' + (order.receipt_id || order.order_number || order.id),
            customer: order.customer,
            country: order.shipping_country || order.country_iso || '—',
            placedLabel: this.formatPlaced(order.created_at),
            days: this.daysUntil(order.shipping_expected_at),
            need,
            alloc,
            auto: alloc > 0,
          });
        }
        this.orders = rows;
        this.draft = rows.map((row) => ({ ...row }));
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.orders = [];
        this.draft = [];
      },
    });
  }

  close() {
    this.closed.emit();
  }

  get used(): number {
    return this.draft.reduce((sum, row) => sum + row.alloc, 0);
  }

  get free(): number {
    return this.stock - this.used;
  }

  get totalNeed(): number {
    return this.orders.reduce((sum, row) => sum + row.need, 0);
  }

  canMinus(row: ReallocOrderRow): boolean {
    return row.alloc > 0;
  }

  canPlus(row: ReallocOrderRow): boolean {
    return this.free > 0 && row.alloc < row.need;
  }

  minus(index: number) {
    const row = this.draft[index];
    if (row && this.canMinus(row)) {
      row.alloc--;
    }
  }

  plus(index: number) {
    const row = this.draft[index];
    if (row && this.canPlus(row)) {
      row.alloc++;
    }
  }

  changed(index: number): boolean {
    const draft = this.draft[index];
    const orig = this.orders[index];
    return !!draft && !!orig && draft.alloc !== orig.alloc;
  }

  get hasChange(): boolean {
    return this.draft.some((_, index) => this.changed(index));
  }

  get summary(): string {
    let taken = 0;
    let given = 0;
    const donorIds: string[] = [];
    const recipientIds: string[] = [];
    this.draft.forEach((row, index) => {
      const delta = row.alloc - (this.orders[index]?.alloc ?? row.alloc);
      if (delta < 0) {
        taken += -delta;
        donorIds.push(row.displayId);
      }
      if (delta > 0) {
        given += delta;
        recipientIds.push(row.displayId);
      }
    });
    if (taken === 0 && given === 0) {
      return 'Még nincs módosítás — vedd el a darabot a − gombbal, és add át a + gombbal.';
    }
    const parts: string[] = [];
    if (given > 0) {
      parts.push(given + ' db átruházva: ' + donorIds.join(', ') + ' → ' + recipientIds.join(', '));
    } else {
      parts.push(taken + ' db visszakerül a szabad készletbe: ' + donorIds.join(', '));
    }
    if (this.free > 0) {
      parts.push(this.free + ' db még kiosztható');
    }
    return parts.join(' · ');
  }

  deadlineLabel(days: number | null): string {
    if (days === null) {
      return 'nincs feladási határidő';
    }
    if (days < 0) {
      return Math.abs(days) + ' napja késik a feladás';
    }
    if (days === 0) {
      return 'ma kell feladni';
    }
    return days + ' nap van a feladásig';
  }

  deadlineTone(days: number | null): string {
    if (days === null) {
      return 'grey';
    }
    if (days < 0) {
      return 'red';
    }
    return days === 0 ? 'yellow' : 'green';
  }

  save() {
    if (!this.hasChange || !this.product) {
      return;
    }
    const payload = {
      component_id: this.product.id,
      sku: this.product.sku,
      allocations: this.draft.map((row) => ({ order_id: row.orderId, quantity: row.alloc })),
    };
    this.http.post('orders/stock-reallocation', payload).subscribe({
      next: () => {
        this.notificationService.show('Készlet átruházás elmentve!', 'success');
        this.saved.emit();
      },
      error: () => {
        this.notificationService.show('Az átruházás mentése sikertelen!', 'danger');
      },
    });
  }

  private formatPlaced(value: string | Date): string {
    if (!value) {
      return '—';
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return '—';
    }
    const mm = `${d.getMonth() + 1}`.padStart(2, '0');
    const dd = `${d.getDate()}`.padStart(2, '0');
    const hh = `${d.getHours()}`.padStart(2, '0');
    const mi = `${d.getMinutes()}`.padStart(2, '0');
    return `${d.getFullYear()}. ${mm}. ${dd}. ${hh}:${mi}`;
  }

  private daysUntil(value?: string | Date | null): number | null {
    if (!value) {
      return null;
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return null;
    }
    const today = new Date();
    const a = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
    const b = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
    return Math.round((a - b) / (1000 * 60 * 60 * 24));
  }
}
